import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Tabs,
  Tab,
} from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import AppLoading from "verticales/AppLoading.tsx";
import MDButton from "components/MDButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import CloudIcon from "@mui/icons-material/Cloud";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";
import fetchData from "verticales/FetchData";
import MDBadge from "components/MDBadge";

/* ============================
   Badge Envío Usuarios
============================ */

const TieneUsuarioBadge = ({ row }) => {
  const valor = row?.original?.tiene_usuario;

  const config =
    valor === "S" ? { label: "Envia", color: "info" } : { label: "No Envia", color: "error" };

  return (
    <MDBadge
      badgeContent={config.label}
      color={config.color}
      variant="gradient"
      size="sm"
      sx={{
        minWidth: 90,
        justifyContent: "center",
        display: "flex",
        textAlign: "center",
        px: 1,
      }}
    />
  );
};

TieneUsuarioBadge.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      tiene_usuario: PropTypes.string,
    }),
  }).isRequired,
};

/* ============================
   Badge Estado EC2
============================ */

const EstadoEC2Badge = ({ row }) => {
  const estadoRaw = row?.original?.estado_ec2;
  const estado = estadoRaw ? estadoRaw.toLowerCase() : "desconocido";

  let config = {
    label: estado.toUpperCase(),
    color: "secondary",
  };

  if (estado === "running") {
    config = { label: "RUNNING", color: "success" };
  } else if (estado === "stopped") {
    config = { label: "STOPPED", color: "error" };
  }

  return (
    <MDBadge
      badgeContent={config.label}
      color={config.color}
      variant="gradient"
      size="sm"
      sx={{
        minWidth: 100,
        justifyContent: "center",
        display: "flex",
        textAlign: "center",
        px: 1,
      }}
    />
  );
};

EstadoEC2Badge.propTypes = {
  row: PropTypes.object.isRequired,
};

/* ============================
   Metric Card Component
============================ */

const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card
    sx={{
      height: "100%",
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0 8px 24px ${color}25`,
      },
    }}
  >
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: `${color}20`,
            display: "flex",
            mr: 2,
          }}
        >
          <Icon sx={{ color: color, fontSize: 32 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

/* ============================
   Componente Principal
============================ */

const ViewAutoScaling = ({ data }) => {
  const [loadingState, setLoadingState] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [chartsExpanded, setChartsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (data && data.length > 0) {
      setTableData(data);

      if (isFirstLoad.current) {
        setLoadingState(false);
        isFirstLoad.current = false;
      }
    } else {
      setLoadingState(true);
    }
  }, [data]);

  // Cálculos de métricas
  const metrics = useMemo(() => {
    const totalInstancias = tableData.length;
    const running = tableData.filter((row) => row.estado_ec2?.toLowerCase() === "running").length;
    const stopped = tableData.filter((row) => row.estado_ec2?.toLowerCase() === "stopped").length;
    const conUsuarios = tableData.filter((row) => row.tiene_usuario === "S").length;
    const enable = tableData.filter((row) => row.estado_asg?.toLowerCase() === "enable").length;
    const disabled = tableData.filter((row) => row.estado_asg?.toLowerCase() === "disabled").length;
    const totalUsuarios = tableData.reduce(
      (sum, row) => sum + (parseInt(row.total_usuarios) || 0),
      0
    );
    const clientesUnicos = new Set(tableData.map((row) => row.cliente).filter(Boolean)).size;

    return {
      totalInstancias,
      running,
      stopped,
      conUsuarios,
      totalUsuarios,
      enable,
      disabled,
      clientesUnicos,
    };
  }, [tableData]);

  // Datos para gráfico de estados EC2
  const estadoEC2Data = useMemo(() => {
    const estados = {};
    tableData.forEach((row) => {
      const estado = row.estado_ec2?.toLowerCase() || "desconocido";
      estados[estado] = (estados[estado] || 0) + 1;
    });

    return Object.entries(estados).map(([key, value]) => ({
      name: key.toUpperCase(),
      value: value,
    }));
  }, [tableData]);

  // Datos para gráfico de clientes
  const clientesData = useMemo(() => {
    const clientes = {};
    tableData.forEach((row) => {
      const cliente = row.cliente || "Sin cliente";
      clientes[cliente] = (clientes[cliente] || 0) + 1;
    });

    return Object.entries(clientes)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 clientes
  }, [tableData]);

  // Datos para gráfico de cuentas
  const cuentasData = useMemo(() => {
    const cuentas = {};
    tableData.forEach((row) => {
      const cuenta = row.account || "Sin cuenta";
      cuentas[cuenta] = (cuentas[cuenta] || 0) + 1;
    });

    return Object.entries(cuentas)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [tableData]);

  const columns = useMemo(
    () => [
      { accessorKey: "account", header: "Cuenta", size: 150 },
      { accessorKey: "cliente", header: "Cliente", size: 150 },
      { accessorKey: "instance_id", header: "Instance Id", size: 200 },
      { accessorKey: "principal", header: "EC2 principal", size: 200 },
      { accessorKey: "estado_asg", header: "Estado (DB)", size: 150 },
      {
        accessorKey: "estado_ec2",
        header: "Estado EC2",
        size: 180,
        Cell: EstadoEC2Badge,
      },
      { accessorKey: "total_usuarios", header: "Usuarios", size: 150 },
      {
        accessorKey: "tiene_usuario",
        header: "Envio usuarios",
        size: 200,
        Cell: TieneUsuarioBadge,
      },
    ],
    []
  );

  const handleExportData = () => {
    if (tableData.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }
    alert("Exportando datos...");
  };

  const COLORS = {
    running: "#4caf50",
    stopped: "#f44336",
    desconocido: "#9e9e9e",
    pending: "#ff9800",
  };

  const PIE_COLORS = ["#1976d2", "#dc004e", "#ff9800", "#4caf50", "#9c27b0", "#00bcd4"];

  if (loadingState) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 200px)",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <AppLoading titleCustom={"Agente Usuarios AutoScaling"} />
      </Box>
    );
  }

  const renderDashboard = () => (
    <Box>
      {/* Métricas principales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Instancias"
            value={metrics.totalInstancias}
            icon={CloudIcon}
            color="#1976d2"
            subtitle="Instancias EC2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Running"
            value={metrics.running}
            icon={PlayCircleOutlineIcon}
            color="#4caf50"
            subtitle={`${((metrics.running / metrics.totalInstancias) * 100 || 0).toFixed(
              1
            )}% activas`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Stopped"
            value={metrics.stopped}
            icon={StopCircleIcon}
            color="#f44336"
            subtitle={`${((metrics.stopped / metrics.totalInstancias) * 100 || 0).toFixed(
              1
            )}% detenidas`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Usuarios"
            value={metrics.totalUsuarios}
            icon={GroupIcon}
            color="#9c27b0"
            subtitle={`${metrics.conUsuarios} instancias envían`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="ASG Habilitadas"
            value={metrics.enable}
            icon={ToggleOnIcon}
            color="#00bcd4"
            subtitle={`${((metrics.enable / metrics.totalInstancias) * 100 || 0).toFixed(
              1
            )}% habilitadas`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="ASG Deshabilitadas"
            value={metrics.disabled}
            icon={ToggleOffIcon}
            color="#ff9800"
            subtitle={`${((metrics.disabled / metrics.totalInstancias) * 100 || 0).toFixed(
              1
            )}% deshabilitadas`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Grupos ASG"
            value={metrics.clientesUnicos}
            icon={BusinessIcon}
            color="#3f51b5"
            subtitle={`de ${metrics.totalInstancias} instancias`}
          />
        </Grid>
      </Grid>

      {/* Gráficos colapsables */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: chartsExpanded ? "1px solid #e0e0e0" : "none",
            cursor: "pointer",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
          onClick={() => setChartsExpanded(!chartsExpanded)}
        >
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DashboardIcon /> Análisis Gráfico
          </Typography>
          <IconButton size="small">
            {chartsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={chartsExpanded}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Gráfico de Estados EC2 */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Estados EC2
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={estadoEC2Data}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {estadoEC2Data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            COLORS[entry.name.toLowerCase()] ||
                            PIE_COLORS[index % PIE_COLORS.length]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>

              {/* Gráfico de Top Clientes */}
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Top 10 Instancias por Cliente
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={clientesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#1976d2" name="Instancias" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              {/* Gráfico de Cuentas */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Distribución por Cuentas
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={cuentasData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#4caf50" name="Instancias" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );

  const renderTable = () => (
    <TableMaestros
      height={300}
      arrayColumns={columns}
      data={tableData}
      enableGrouping
      enableStickyHeader
      enableColumnResizing
      muiTableContainerProps={{
        sx: { maxHeight: "calc(100vh - 445px)", minHeight: "150px" },
      }}
      muiTableBodyCellProps={{
        sx: { borderRight: "1px solid #e0e0e0", fontSize: 14, padding: "0.5rem" },
      }}
      muiTableHeadCellProps={{
        sx: (theme) => ({
          backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
          fontSize: "14px",
          fontWeight: "bold",
        }),
      }}
      renderTopToolbarCustomActions={() => (
        <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
          <MDButton
            onClick={handleExportData}
            startIcon={<FileDownloadIcon />}
            sx={{ fontSize: "12px", fontWeight: "normal" }}
          >
            Exportar Datos
          </MDButton>
        </Box>
      )}
      initialState={{
        density: "compact",
        pagination: { pageIndex: 0, pageSize: 50 },
      }}
    />
  );

  const renderCombinedView = () => (
    <>
      {renderDashboard()}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <TableChartIcon /> Detalle de Instancias
          </Typography>
          {renderTable()}
        </CardContent>
      </Card>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 200px)",
        overflow: "hidden",
      }}
    >
      {/* Tabs de navegación */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<ViewComfyIcon />} label="Vista Combinada" iconPosition="start" />
          <Tab icon={<DashboardIcon />} label="Solo Dashboard" iconPosition="start" />
          <Tab icon={<TableChartIcon />} label="Solo Tabla" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Contenido según tab activo */}
      <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
        {activeTab === 0 && renderCombinedView()}
        {activeTab === 1 && renderDashboard()}
        {activeTab === 2 && renderTable()}
      </Box>
    </Box>
  );
};

ViewAutoScaling.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      account: PropTypes.string,
      instance_id: PropTypes.string.isRequired,
      instance_name: PropTypes.string,
      ip_privada: PropTypes.string,
      ip_publica: PropTypes.string,
      cliente: PropTypes.string,
      principal: PropTypes.string,
      estado_asg: PropTypes.string,
      estado_ec2: PropTypes.string,
      tiene_usuario: PropTypes.string,
    })
  ),
};

export default ViewAutoScaling;
